import { FeatureKinds } from './featureKind';
import { FeatureSwitchTable } from './featureSwitchesCache';
import { governingSwitchState } from './governingSwitchState';
import { resolveEntitlement, resolveFeature } from './resolveFeature';

/** The plan whose features a beta tester gains on top of its own. */
export const BETA_PLAN = 'beta';

export type EffectiveFeaturesInput = {
  /** `users.available_features`, straight off the row: jsonb, so `unknown`. */
  readonly availableFeatures?: unknown;
  readonly planFeatures?: readonly string[] | null;
  /** The `beta` plan's features, applied only when `betaTester`. */
  readonly betaFeatures?: readonly string[] | null;
  readonly betaTester?: boolean;
  readonly email?: string | null;
  readonly accountId: number;
  readonly switches: FeatureSwitchTable;
  readonly featureKinds?: FeatureKinds;
};

function featureStrings(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function grantsFrom(input: EffectiveFeaturesInput): string[] {
  return [
    ...new Set([
      ...featureStrings(input.availableFeatures),
      ...(input.planFeatures ?? []),
      ...(input.betaTester ? input.betaFeatures ?? [] : []),
    ]),
  ];
}

/**
 * Every feature an account may use: what it is granted, narrowed or widened by
 * how widely each feature is open.
 *
 * One function for both enforcers. `dashboard-api` answers `/me` with it and
 * rest-api's guards decide with it, so a feature closed in the row cannot stay
 * open in the API — which is what a kill switch has to mean.
 */
export function effectiveFeaturesFor(input: EffectiveFeaturesInput): string[] {
  const grants = grantsFrom(input);
  const listedInTheRow = input.switches.active ? Object.keys(input.switches.switches) : [];
  const candidates = [...new Set([...grants, ...listedInTheRow])];

  return candidates.filter((feature) => {
    const state = governingSwitchState(feature, input.switches, input.featureKinds);

    return state === undefined
      ? resolveEntitlement(feature, grants)
      : resolveFeature(feature, { state, email: input.email, features: grants, accountId: input.accountId });
  });
}

export function holdsFeature(feature: string, input: EffectiveFeaturesInput): boolean {
  return effectiveFeaturesFor(input).includes(feature);
}
